import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Button,
  Alert,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  IconButton,
  Tooltip,
  Chip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import InfoIcon from '@mui/icons-material/Info';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import CommentIcon from '@mui/icons-material/Comment';
import { Post } from '../types/interfaces';
import { SpreadsheetPlain } from '../spreadsheet/src';
import { ColumnDef, ColumnOrderState } from '@tanstack/react-table';
import {
  useDeletePostMutation,
  useBulkDeletePostsMutation,
  useDeleteExpiredPostsMutation,
  useDeleteDeletedPostsMutation,
  useGetPostCountsQuery,
  useDeleteOrphanedCommentsMutation,
  useDeleteMockPostsMutation,
  useUpdatePostMutation,
  useGetPostFilterValuesQuery
} from '../redux/adminApiSlice';
import { adminDebug } from '../utils/debug';
import { usePostCounts } from '../hooks/usePostCounts';
import { useInfinitePosts } from '../hooks/useInfinitePosts';
import { useCopyToClipboard, useFieldEditor, useTableFilters, useDeleteWithConfirmation, useSpreadsheetData } from '../hooks';
import LocationModal, { LocationData } from '../components/LocationModal';
import PostDetails from '../components/PostDetails';

// Extend Post interface to include id for Spreadsheet
interface PostWithId extends Post {
  id: string;
}

// Helper function to format expiry time
const formatExpiryTime = (expiresAt: string | null | undefined): string => {
  if (!expiresAt) return 'INF';
  
  const now = new Date();
  const expiry = new Date(expiresAt);
  
  if (expiry <= now) return 'Expired';
  
  const diff = expiry.getTime() - now.getTime();
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  
  if (days > 0) {
    return hours > 0 ? `${days}d ${hours}h` : `${days}d`;
  }
  return `${hours}h`;
};

// Helper function to format time ago
const formatTimeAgo = (date: Date | string): string => {
  const now = new Date();
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  const diff = now.getTime() - dateObj.getTime();
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  
  if (days > 0) {
    return `${days}d ago`;
  }
  return hours > 0 ? `${hours}h ago` : 'Just now';
};


const Posts: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  
  const hasValidationIssues = (post: Post) => {
    return !post.user || (typeof post.user === 'string' && !post.user);
  };

  const [nullUserPosts, setNullUserPosts] = useState<Post[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  // Get current tab from URL parameter
  const getCurrentTab = () => {
    const tab = searchParams.get('tab');
    return tab === 'null-users' ? 'null-users' : 'all';
  };
  const [searchQuery, setSearchQuery] = useState('');
  const [locationIdFilter, setLocationIdFilter] = useState('');
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [excludeComments, setExcludeComments] = useState(false);
  const [excludeExpired, setExcludeExpired] = useState(false);
  const [selectedPost, setSelectedPost] = useState<Post | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<{ post: PostWithId; locationData: LocationData } | null>(null);

  const handleLocationClick = useCallback((location: any, locationLevel: string) => {
    if (!selectedLocation) return;

    // Build locationData from the clicked location
    // Set the clicked location at its level and keep parent levels from existing data
    const existingLocationData = selectedLocation.locationData;
    const newLocationData: LocationData = {
      place: undefined, // Places are not locations
      neighborhood: locationLevel === 'neighborhood' ? location : existingLocationData.neighborhood,
      city: locationLevel === 'city' ? location : existingLocationData.city,
      state: locationLevel === 'state' ? location : existingLocationData.state,
      country: locationLevel === 'country' ? location : existingLocationData.country,
    };

    // Clear child levels when clicking on a parent level
    if (locationLevel === 'country') {
      newLocationData.state = undefined;
      newLocationData.city = undefined;
      newLocationData.neighborhood = undefined;
    } else if (locationLevel === 'state') {
      newLocationData.city = undefined;
      newLocationData.neighborhood = undefined;
    } else if (locationLevel === 'city') {
      newLocationData.neighborhood = undefined;
    }

    // Update the selected location with new locationData
    setSelectedLocation({
      post: selectedLocation.post,
      locationData: newLocationData
    });
  }, [selectedLocation]);

  const [deleteExpiredPosts, { isLoading: isExpiredDeleting }] = useDeleteExpiredPostsMutation();
  const [deleteDeletedPosts, { isLoading: isDeletedDeleting }] = useDeleteDeletedPostsMutation();
  const [deleteOrphanedComments, { isLoading: isOrphanedCommentsDeleting }] = useDeleteOrphanedCommentsMutation();
  const [deleteMockPosts, { isLoading: isMockPostsDeleting }] = useDeleteMockPostsMutation();
  const [deletePost] = useDeletePostMutation();
  const [bulkDeletePosts, { isLoading: isBulkDeleting }] = useBulkDeletePostsMutation();
  const [updatePost] = useUpdatePostMutation();

  // Hooks
  const copyToClipboard = useCopyToClipboard();
  const { columnFilters, setColumnFilters } = useTableFilters();
  const [columnOrder, setColumnOrder] = useState<ColumnOrderState>([]);
  
  const { 
    expiredCount, 
    deletedCount, 
    orphanedCommentsCount, 
    refetchCounts 
  } = usePostCounts(30000); // Refresh every 30 seconds

  const handleOpenInfo = useCallback((post: Post) => {
    setSelectedPost(post);
  }, []);

  // Fetch unique filter values from all posts
  const { data: filterValuesData } = useGetPostFilterValuesQuery({
    excludeComments: excludeComments || undefined,
    excludeExpired: excludeExpired || undefined,
    includeDeleted: includeDeleted || undefined,
  });

  // Use infinite query for posts with filters
  const {
    data: postsData,
    isLoading: isLoadingPosts,
    isFetching: isFetchingPosts,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    error: postsError,
    refetch,
  } = useInfinitePosts({
    locationId: locationIdFilter || undefined,
    includeDeleted: includeDeleted || undefined,
    excludeComments: excludeComments || undefined,
    excludeExpired: excludeExpired || undefined,
    userFilter: columnFilters.user && columnFilters.user.length > 0 ? columnFilters.user : undefined,
    mediaFilter: columnFilters.media && columnFilters.media.length > 0 ? columnFilters.media : undefined,
    originLevelFilter: columnFilters.originLevel && columnFilters.originLevel.length > 0 ? columnFilters.originLevel : undefined,
    currentPostLevelFilter: columnFilters.currentPostLevel && columnFilters.currentPostLevel.length > 0 ? columnFilters.currentPostLevel : undefined,
    locationNameFilter: columnFilters.location && columnFilters.location.length > 0 ? columnFilters.location : undefined,
  });

  // Flatten all pages into a single array
  const posts = useMemo(() => {
    if (!postsData?.pages) return [];
    return postsData.pages.flatMap(page => page.data || []);
  }, [postsData]);

  // Get total count from first page
  const totalPosts = useMemo(() => {
    return postsData?.pages[0]?.total || 0;
  }, [postsData]);

  const fieldEditor = useFieldEditor<PostWithId>({
    onUpdate: async (id, field, value) => {
      const updates: Partial<Post> = {};
      (updates as any)[field] = value;
      await updatePost({ id, updates }).unwrap();
    },
    onRefetch: async () => {
      await refetch();
    },
  });

  const { deleteItem: handleDeletePost } = useDeleteWithConfirmation({
    onDelete: async (id) => {
      const result = await deletePost(id).unwrap();
      if (!result.success) {
        throw new Error(result.message || 'Failed to delete post');
      }
      return result;
    },
    onSuccess: async (id) => {
      // Invalidate and refetch the infinite query to immediately update the list
      await queryClient.invalidateQueries({ queryKey: ['posts', 'infinite'] });
      await refetch();
      setNullUserPosts(posts => posts.filter(post => post._id !== id));
      localStorage.removeItem(`post-${id}`);
      window.dispatchEvent(new CustomEvent('post-deleted', { detail: { postId: id } }));
      setError(null);
      adminDebug.posts(`Successfully deleted post: ${id}`);
    },
    onError: (error, id) => {
      const errorMsg = error instanceof Error ? error.message : 'Error deleting post';
      adminDebug.error(`Delete post error: ${errorMsg}`);
      setError(errorMsg);
    },
    confirmationMessage: 'Are you sure you want to delete this post? This action cannot be undone.',
    errorMessage: 'Failed to delete post',
  });

  const handleBulkDelete = useCallback(async () => {
    if (selectedRows.size === 0) {
      setError('No posts selected');
      return;
    }

    const confirmMessage = `Are you sure you want to delete ${selectedRows.size} post${selectedRows.size > 1 ? 's' : ''}? This action cannot be undone.`;
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      const postIds = Array.from(selectedRows);
      const result = await bulkDeletePosts(postIds).unwrap();
      
      if (result.success) {
        // Invalidate and refetch the infinite query to immediately update the list
        await queryClient.invalidateQueries({ queryKey: ['posts', 'infinite'] });
        await refetch();
        setSelectedRows(new Set());
        setError(null);
        
        if (result.errors && result.errors.length > 0) {
          adminDebug.error(`Bulk delete completed with errors: ${result.errors.join(', ')}`);
          setError(`Deleted ${result.deletedCount} posts. Some errors occurred: ${result.errors.slice(0, 3).join(', ')}${result.errors.length > 3 ? '...' : ''}`);
        } else {
          adminDebug.posts(`Successfully bulk deleted ${result.deletedCount} posts`);
        }
      } else {
        throw new Error(result.message || 'Failed to delete posts');
      }
    } catch (error: any) {
      const errorMsg = error instanceof Error ? error.message : 'Error bulk deleting posts';
      adminDebug.error(`Bulk delete error: ${errorMsg}`);
      setError(errorMsg);
    }
  }, [selectedRows, bulkDeletePosts, queryClient, refetch]);



  const [showDebugInfo, setShowDebugInfo] = useState(false);
  const { data: postCountsData } = useGetPostCountsQuery(undefined, {
    skip: !showDebugInfo
  });

  const [isDeletingMockPosts, setIsDeletingMockPosts] = useState(false);
  const [showDeleteMockConfirmation, setShowDeleteMockConfirmation] = useState(false);

  // Transform posts to include id field using hook
  const { transformedData: postsWithId } = useSpreadsheetData<Post>({
    data: posts,
    columnFilters: {}, // Column filters are handled on backend
  });


  // Define columns
  const columns: ColumnDef<PostWithId>[] = useMemo(() => {
    // Level order for sorting (ascending: place -> country, descending: country -> place)
    const levelOrder: Record<string, number> = {
      'PLACE': 1,
      'NEIGHBORHOOD': 2,
      'CITY': 3,
      'STATE': 4,
      'COUNTRY': 5,
    };

    return [
    {
      id: 'select',
      header: ({ table }) => {
        const allRows = table.getRowModel().rows;
        const allSelected = allRows.length > 0 && allRows.every(row => selectedRows.has(row.original.id));
        const someSelected = allRows.some(row => selectedRows.has(row.original.id));
        return (
          <Checkbox
            checked={allSelected}
            indeterminate={someSelected && !allSelected}
            onChange={(e) => {
              if (e.target.checked) {
                setSelectedRows(new Set(allRows.map(row => row.original.id)));
              } else {
                setSelectedRows(new Set());
              }
            }}
            size="small"
          />
        );
      },
      cell: ({ row }) => (
        <Checkbox
          checked={selectedRows.has(row.original.id)}
          onChange={(e) => {
            e.stopPropagation();
            const newSelected = new Set(selectedRows);
            if (e.target.checked) {
              newSelected.add(row.original.id);
            } else {
              newSelected.delete(row.original.id);
            }
            setSelectedRows(newSelected);
          }}
          size="small"
        />
      ),
      size: 50,
      minSize: 50,
      maxSize: 50,
      enableSorting: false,
    },
    {
      accessorKey: '_id',
      header: 'ID',
      size: 140,
      minSize: 120,
      maxSize: 160,
      enableSorting: true,
      cell: ({ getValue }) => {
        const id = getValue() as string;
        const shortId = id.substring(0, 8) + '...';
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Tooltip title={id}>
              <Typography variant="body2" noWrap sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                {shortId}
              </Typography>
            </Tooltip>
            <IconButton size="small" onClick={() => copyToClipboard(id)}>
              <ContentCopyIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Box>
        );
      },
    },
    {
      accessorKey: 'text',
      header: 'Text',
      size: 300,
      minSize: 200,
      maxSize: 500,
      enableSorting: true,
      sortingFn: (rowA, rowB) => {
        const textA = (rowA.getValue('text') as string) || '';
        const textB = (rowB.getValue('text') as string) || '';
        return textA.localeCompare(textB, undefined, { sensitivity: 'base' });
      },
      cell: ({ getValue, row }) => {
        const text = getValue() as string;
        const post = row.original;
        return (
          <Typography
            variant="body2"
            noWrap
            title={text}
            onClick={() => fieldEditor.handleEditField(post, 'text', 'Text')}
            sx={{
              cursor: 'pointer',
              '&:hover': { textDecoration: 'underline', color: 'primary.main' }
            }}
          >
            {text || '-'}
          </Typography>
        );
      },
    },
    {
      accessorKey: 'user',
      header: 'User',
      size: 150,
      minSize: 120,
      maxSize: 200,
      enableSorting: true,
      sortingFn: (rowA, rowB) => {
        const postA = rowA.original;
        const postB = rowB.original;
        const nameA = typeof postA.user === 'object' && postA.user ? 
          (postA.user.displayName || 'Unknown User') : 'Unknown User';
        const nameB = typeof postB.user === 'object' && postB.user ? 
          (postB.user.displayName || 'Unknown User') : 'Unknown User';
        return nameA.localeCompare(nameB, undefined, { sensitivity: 'base' });
      },
      cell: ({ row }) => {
        const post = row.original;
        if (hasValidationIssues(post)) {
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Chip label="Invalid User" size="small" color="error" />
              <Tooltip title="Post has invalid or missing user reference">
                <IconButton size="small" color="error">
                  <ErrorOutlineIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            </Box>
          );
        }
        const displayName = typeof post.user === 'object' && post.user ? 
          post.user.displayName : 
          'Unknown User';
        return (
          <Typography variant="body2" noWrap title={displayName}>
            {displayName}
          </Typography>
        );
      },
    },
    {
      accessorKey: 'createdAt',
      header: 'Created',
      size: 130,
      minSize: 100,
      maxSize: 150,
      enableSorting: true,
      sortingFn: (rowA, rowB) => {
        const dateA = new Date(rowA.getValue('createdAt') as string).getTime();
        const dateB = new Date(rowB.getValue('createdAt') as string).getTime();
        return dateA - dateB;
      },
      cell: ({ getValue }) => {
        const date = getValue() as string;
        const formatted = formatTimeAgo(date);
        return (
          <Typography variant="body2" noWrap title={new Date(date).toLocaleString()}>
            {formatted}
          </Typography>
        );
      },
    },
    {
      accessorKey: 'expiresAt',
      header: 'Expires In',
      size: 120,
      minSize: 100,
      maxSize: 150,
      enableSorting: true,
      sortingFn: (rowA, rowB) => {
        const dateA = rowA.getValue('expiresAt') as string | null | undefined;
        const dateB = rowB.getValue('expiresAt') as string | null | undefined;
        // Treat null/undefined as infinity (sort to end)
        if (!dateA && !dateB) return 0;
        if (!dateA) return 1;
        if (!dateB) return -1;
        return new Date(dateA).getTime() - new Date(dateB).getTime();
      },
      cell: ({ getValue }) => {
        const date = getValue() as string | null | undefined;
        const formatted = formatExpiryTime(date);
        return (
          <Typography variant="body2" noWrap>
            {formatted}
          </Typography>
        );
      },
    },
    {
      id: 'media',
      header: 'Media',
      size: 80,
      minSize: 70,
      maxSize: 100,
      enableSorting: true,
      accessorFn: (row) => {
        if (!row.media || !row.media.type) return 'None';
        return row.media.type === 'photo' ? 'Photo' : 'Video';
      },
      sortingFn: (rowA, rowB) => {
        const mediaA = rowA.getValue('media') as string;
        const mediaB = rowB.getValue('media') as string;
        return mediaA.localeCompare(mediaB, undefined, { sensitivity: 'base' });
      },
      cell: ({ row }) => {
        const post = row.original;
        if (!post.media || !post.media.type) {
          return <Chip label="None" size="small" variant="outlined" />;
        }
        return (
          <Tooltip title={`${post.media.width}x${post.media.height} (${post.media.aspectRatio})`}>
            <Chip 
              label={post.media.type === 'photo' ? 'Photo' : 'Video'} 
              size="small" 
              color={post.media.type === 'photo' ? 'primary' : 'secondary'}
              variant="filled"
            />
          </Tooltip>
        );
      },
    },
    {
      accessorKey: 'originLevel',
      header: 'Origin Level',
      size: 130,
      minSize: 120,
      maxSize: 150,
      enableSorting: true,
      sortingFn: (rowA, rowB) => {
        const levelA = (rowA.getValue('originLevel') as string) || '';
        const levelB = (rowB.getValue('originLevel') as string) || '';
        const orderA = levelOrder[levelA.toUpperCase()] || 0;
        const orderB = levelOrder[levelB.toUpperCase()] || 0;
        return orderA - orderB;
      },
      cell: ({ getValue }) => {
        const level = getValue() as string;
        return (
          <Chip label={level} size="small" variant="outlined" />
        );
      },
    },
    {
      accessorKey: 'currentPostLevel',
      header: 'Current Level',
      size: 130,
      minSize: 120,
      maxSize: 150,
      enableSorting: true,
      sortingFn: (rowA, rowB) => {
        const levelA = (rowA.getValue('currentPostLevel') as string) || '';
        const levelB = (rowB.getValue('currentPostLevel') as string) || '';
        const orderA = levelOrder[levelA.toUpperCase()] || 0;
        const orderB = levelOrder[levelB.toUpperCase()] || 0;
        return orderA - orderB;
      },
      cell: ({ getValue }) => {
        const level = getValue() as string;
        return (
          <Chip label={level} size="small" color="primary" variant="outlined" />
        );
      },
    },
    {
      id: 'location',
      header: 'Location',
      size: 200,
      minSize: 150,
      maxSize: 300,
      enableSorting: true,
      accessorFn: (row) => {
        const level = row.originLevel?.toLowerCase();
        const locationData = row.location && row.location[level as keyof typeof row.location];
        if (!locationData || typeof locationData !== 'object' || !('name' in locationData)) {
          return 'No Location';
        }
        return locationData.name || 'No Location';
      },
      sortingFn: (rowA, rowB) => {
        const nameA = (rowA.getValue('location') as string) || '';
        const nameB = (rowB.getValue('location') as string) || '';
        return nameA.localeCompare(nameB, undefined, { sensitivity: 'base' });
      },
      cell: ({ row }) => {
        const post = row.original;
        const level = post.originLevel.toLowerCase();
        const locationData = post.location && post.location[level as keyof typeof post.location];
        const icons: Record<string, string> = {
          place: '📍',
          neighborhood: '🏘️',
          city: '🌆',
          state: '🗺️',
          country: '🌎'
        };
        
        if (!post.location) {
          return <Chip label="No Location" size="small" color="error" />;
        }
        if (!locationData) {
          return <Chip label="Invalid Location" size="small" color="error" />;
        }
        if (typeof locationData === 'object' && 'name' in locationData) {
          const icon = icons[level] || '📍';
          return (
            <Tooltip title={level.charAt(0).toUpperCase() + level.slice(1)}>
              <Typography
                variant="body2"
                noWrap
                onClick={() => {
                  setSelectedLocation({ post, locationData: post.location });
                }}
                sx={{
                  cursor: 'pointer',
                  '&:hover': { textDecoration: 'underline', color: 'primary.main' }
                }}
              >
                {icon} {locationData.name}
              </Typography>
            </Tooltip>
          );
        }
        return <Chip label="Unknown Location" size="small" color="error" />;
      },
    },
    {
      accessorKey: 'totalPromotes',
      header: () => <ArrowUpwardIcon fontSize="small" />,
      size: 50,
      minSize: 45,
      maxSize: 65,
      enableSorting: true,
      sortingFn: (rowA, rowB) => {
        const countA = (rowA.getValue('totalPromotes') as number) || 0;
        const countB = (rowB.getValue('totalPromotes') as number) || 0;
        return countA - countB;
      },
      cell: ({ getValue }) => {
        const count = getValue() as number;
        return <Typography variant="body2" noWrap>{count || 0}</Typography>;
      },
    },
    {
      accessorKey: 'promotePercentage',
      header: '%',
      size: 50,
      minSize: 45,
      maxSize: 65,
      enableSorting: true,
      sortingFn: (rowA, rowB) => {
        const percentA = (rowA.getValue('promotePercentage') as number) || 0;
        const percentB = (rowB.getValue('promotePercentage') as number) || 0;
        return percentA - percentB;
      },
      cell: ({ getValue }) => {
        const percentage = getValue() as number;
        return <Typography variant="body2">{Math.round(percentage || 0)}%</Typography>;
      },
    },
    {
      id: 'comments',
      header: () => <CommentIcon fontSize="small" />,
      size: 60,
      minSize: 50,
      maxSize: 80,
      enableSorting: true,
      accessorFn: (row) => {
        return Array.isArray(row.comments) ? row.comments.length : 0;
      },
      sortingFn: (rowA, rowB) => {
        const countA = (rowA.getValue('comments') as number) || 0;
        const countB = (rowB.getValue('comments') as number) || 0;
        return countA - countB;
      },
      cell: ({ row }) => {
        const post = row.original;
        const count = Array.isArray(post.comments) ? post.comments.length : 0;
        return <Typography variant="body2" noWrap>{count}</Typography>;
      },
    },
    {
      id: 'actions',
      header: 'Actions',
      size: 150,
      minSize: 130,
      maxSize: 180,
      enableSorting: false,
      cell: ({ row }) => {
        const post = row.original;
        return (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Tooltip title="View JSON">
              <IconButton
                size="small"
                onClick={() => handleOpenInfo(post)}
                color="primary"
              >
                <InfoIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete Post">
              <IconButton
                size="small"
                onClick={() => handleDeletePost(post._id)}
                color="error"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
    ];
  }, [copyToClipboard, handleOpenInfo, handleDeletePost, fieldEditor, selectedRows]);

  // Set default tab parameter if none exists
  useEffect(() => {
    if (!searchParams.get('tab')) {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('tab', 'all');
      setSearchParams(newSearchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (postsError) {
      console.error('Error details:', postsError);
      setError('Error fetching posts');
    } else {
      setError(null);
    }
  }, [postsError]);

  // Load more posts when needed
  const loadMorePosts = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage && !isFetchingPosts) {
      adminDebug.posts('Loading more posts...');
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, isFetchingPosts, fetchNextPage]);

  const handleDeleteExpiredPosts = async () => {
    try {
      const result = await deleteExpiredPosts().unwrap();
      if (result.success) {
        setError(null);
        await refetchCounts();
        await refetch();
      } else {
        setError(result.message || 'Failed to delete expired posts');
      }
    } catch (err) {
      console.error('Error deleting expired posts:', err);
      setError(err instanceof Error ? err.message : 'Error deleting expired posts');
    }
  };

  const handleDeleteSoftDeletedPosts = async () => {
    try {
      const result = await deleteDeletedPosts().unwrap();
      if (result.success) {
        setError(null);
        await refetchCounts();
        await refetch();
      } else {
        setError(result.message || 'Failed to delete soft-deleted posts');
      }
    } catch (err) {
      console.error('Error deleting soft-deleted posts:', err);
      setError(err instanceof Error ? err.message : 'Error deleting soft-deleted posts');
    }
  };

  const handleDeleteOrphanedComments = async () => {
    try {
      const result = await deleteOrphanedComments().unwrap();
      if (result.success) {
        setError(null);
        await refetchCounts();
        await refetch();
      } else {
        setError(result.message || 'Failed to delete orphaned comments');
      }
    } catch (err) {
      console.error('Error deleting orphaned comments:', err);
      setError(err instanceof Error ? err.message : 'Error deleting orphaned comments');
    }
  };

  const handleDeleteMockPosts = async () => {
    if (isDeletingMockPosts) {
      // If already deleting, cancel the operation
      setIsDeletingMockPosts(false);
      setShowDeleteMockConfirmation(false);
      return;
    }

    try {
      setIsDeletingMockPosts(true);
      const result = await deleteMockPosts().unwrap();
      if (result.success) {
        setError(null);
        await refetchCounts();
        await refetch();
      } else {
        setError(result.message || 'Failed to delete mock posts');
      }
    } catch (err) {
      console.error('Error deleting mock posts:', err);
      setError(err instanceof Error ? err.message : 'Error deleting mock posts');
    } finally {
      setIsDeletingMockPosts(false);
      setShowDeleteMockConfirmation(false);
    }
  };

  // Use filter values from backend (all posts, not just current page)
  const uniqueValues = useMemo(() => {
    if (!filterValuesData) {
      return {
        user: [],
        media: [],
        originLevel: [],
        currentPostLevel: [],
        location: [],
      };
    }
    return filterValuesData;
  }, [filterValuesData]);

  // Filtering is now done on the backend, but we still need client-side search
  const getFilteredPosts = useCallback((postsToFilter: PostWithId[]) => {
    let filtered = postsToFilter;

    // Apply search query filter (client-side only, as it searches text content)
    if (searchQuery) {
      filtered = filtered.filter(post => 
        post._id.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (typeof post.text === 'string' && post.text.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }

    // Column filters are now handled on the backend, so we don't need to filter here
    return filtered;
  }, [searchQuery]);


  return (
    <Box sx={{ 
      width: '100%', 
      maxWidth: '100%', 
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      boxSizing: 'border-box',
      p: 2,
    }}>
      <Typography variant="h4" gutterBottom sx={{ flexShrink: 0 }}>
        Posts Management
      </Typography>

      <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', flexShrink: 0 }}>
        {selectedRows.size > 0 && (
          <Button
            variant="contained"
            color="error"
            onClick={handleBulkDelete}
            disabled={isBulkDeleting}
            startIcon={isBulkDeleting ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            Delete Selected ({selectedRows.size})
          </Button>
        )}
        <Button
          variant="contained"
          color="warning"
          onClick={handleDeleteExpiredPosts}
          disabled={isExpiredDeleting || !expiredCount}
          startIcon={isExpiredDeleting ? <CircularProgress size={20} /> : null}
        >
          Delete Expired Posts ({expiredCount || 0})
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleDeleteSoftDeletedPosts}
          disabled={isDeletedDeleting || !deletedCount}
          startIcon={isDeletedDeleting ? <CircularProgress size={20} /> : null}
        >
          Delete Soft-Deleted Posts ({deletedCount || 0})
        </Button>
        <Button
          variant="contained"
          color="secondary"
          onClick={handleDeleteOrphanedComments}
          disabled={isOrphanedCommentsDeleting || !orphanedCommentsCount}
          startIcon={isOrphanedCommentsDeleting ? <CircularProgress size={20} /> : null}
        >
          Delete Orphaned Comments ({orphanedCommentsCount || 0})
        </Button>
        <Button
          variant="contained"
          color={isDeletingMockPosts ? "error" : "info"}
          onClick={handleDeleteMockPosts}
          disabled={isMockPostsDeleting}
          startIcon={isMockPostsDeleting ? <CircularProgress size={20} /> : null}
        >
          {isDeletingMockPosts ? "Cancel Delete Mock Posts" : "Delete Mock Posts"}
        </Button>
        <Button
          variant="outlined"
          color="info"
          onClick={() => {
            refetchCounts();
            setShowDebugInfo(true);
          }}
        >
          Debug Post Counts
        </Button>
      </Box>

      {showDebugInfo && postCountsData && (
        <Alert severity="info" sx={{ mb: 2, flexShrink: 0 }} onClose={() => setShowDebugInfo(false)}>
          <Typography variant="subtitle1" fontWeight="bold">Post Counts Debug Info</Typography>
          <Box component="pre" sx={{ mt: 1, p: 1, bgcolor: '#f5f5f5', borderRadius: 1, fontSize: '0.85rem' }}>
            {JSON.stringify(postCountsData, null, 2)}
          </Box>
        </Alert>
      )}

      <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', flexShrink: 0 }}>
        <TextField
          size="small"
          placeholder="Search by Post ID..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ width: '250px' }}
        />

        <TextField
          size="small"
          placeholder="Filter by Location ID..."
          value={locationIdFilter}
          onChange={(e) => setLocationIdFilter(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ width: '250px' }}
        />

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={includeDeleted}
                onChange={(e) => setIncludeDeleted(e.target.checked)}
              />
            }
            label="Include Deleted"
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={!excludeComments}
                onChange={(e) => setExcludeComments(!e.target.checked)}
              />
            }
            label="Include Comments"
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={!excludeExpired}
                onChange={(e) => setExcludeExpired(!e.target.checked)}
              />
            }
            label="Include Expired"
          />
        </Box>

        <Button
          variant="outlined"
          onClick={() => {
            setLocationIdFilter('');
            setIncludeDeleted(false);
            setExcludeComments(false);
            setExcludeExpired(false);
            setSearchQuery('');
            refetch();
          }}
        >
          Clear Filters
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2, flexShrink: 0 }}>
          {error}
        </Alert>
      )}

      {getCurrentTab() === 'all' && (
        <Box sx={{ 
          flex: 1,
          width: '100%', 
          maxWidth: '100%', 
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          boxSizing: 'border-box',
        }}>
          <Box sx={{ mb: 2, flexShrink: 0 }}>
            <Typography variant="h4" gutterBottom>
              All Posts {totalPosts > 0 && `(${totalPosts} total)`}
            </Typography>
          </Box>

          {isLoadingPosts && posts.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
              <CircularProgress />
            </Box>
          ) : posts.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
              <Typography>
                No posts found.
              </Typography>
            </Box>
          ) : getFilteredPosts(postsWithId).length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
              <Typography>
                No posts match your search.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ flex: 1, width: '100%', maxWidth: '100%', minHeight: 0 }}>
              <SpreadsheetPlain
                data={getFilteredPosts(postsWithId)}
                columns={columns}
                enableFiltering={true}
                filterableColumns={['user', 'media', 'originLevel', 'currentPostLevel', 'location']}
                uniqueValues={uniqueValues}
                columnFilters={columnFilters}
                onColumnFiltersChange={setColumnFilters}
                enableColumnReordering={true}
                columnOrder={columnOrder}
                onColumnOrderChange={setColumnOrder}
                enableSelection={true}
                selectedRows={selectedRows}
                onSelectionChange={setSelectedRows}
                hasNextPage={hasNextPage ?? false}
                isFetchingNextPage={isFetchingNextPage}
                isFetchingPosts={isFetchingPosts}
                onLoadMore={loadMorePosts}
                hideStatusBar={true}
                hideLoadMoreIndicator={true}
              />
            </Box>
          )}
        </Box>
      )}

      {getCurrentTab() === 'null-users' && (
        <Box sx={{ 
          flex: 1,
          width: '100%', 
          maxWidth: '100%', 
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          boxSizing: 'border-box',
        }}>
          <Box sx={{ mb: 2, flexShrink: 0 }}>
            <Typography variant="h4" gutterBottom>
              Posts with Deleted Users
            </Typography>
          </Box>

          {isLoadingPosts && nullUserPosts.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
              <CircularProgress />
            </Box>
          ) : nullUserPosts.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
              <Typography>
                No posts found with deleted users.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ flex: 1, width: '100%', maxWidth: '100%', minHeight: 0 }}>
              <SpreadsheetPlain
                data={nullUserPosts.map(post => ({ ...post, id: post._id }))}
                columns={columns}
                enableColumnReordering={true}
                columnOrder={columnOrder}
                onColumnOrderChange={setColumnOrder}
              />
            </Box>
          )}
        </Box>
      )}

      {/* JSON Display Dialog */}
      <PostDetails
        isOpen={!!selectedPost}
        onClose={() => setSelectedPost(null)}
        post={selectedPost}
      />

      {/* Edit Field Dialog */}
      <Dialog
        open={!!fieldEditor.editingField}
        onClose={fieldEditor.handleCloseEditModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Edit {fieldEditor.editingField?.label}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <TextField
              label={fieldEditor.editingField?.label}
              value={fieldEditor.editValue}
              onChange={(e) => fieldEditor.setEditValue(e.target.value)}
              fullWidth
              multiline
              rows={4}
              autoFocus
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={fieldEditor.handleDeleteField}
            color="error"
            disabled={fieldEditor.isSaving || !fieldEditor.editValue}
            startIcon={fieldEditor.isSaving ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            Delete
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button onClick={fieldEditor.handleCloseEditModal} disabled={fieldEditor.isSaving}>
            Cancel
          </Button>
          <Button
            onClick={fieldEditor.handleSaveField}
            variant="contained"
            disabled={fieldEditor.isSaving}
            startIcon={fieldEditor.isSaving ? <CircularProgress size={16} /> : null}
          >
            {fieldEditor.isSaving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog
        open={showDeleteMockConfirmation}
        onClose={() => setShowDeleteMockConfirmation(false)}
      >
        <DialogTitle>Confirm Delete Mock Posts</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete all mock posts? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteMockConfirmation(false)}>Cancel</Button>
          <Button 
            onClick={() => {
              setShowDeleteMockConfirmation(false);
              handleDeleteMockPosts();
            }} 
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Location Details Modal */}
      {selectedLocation && (
        <LocationModal
          isOpen={!!selectedLocation}
          onClose={() => setSelectedLocation(null)}
          locationData={selectedLocation.locationData}
          currentPostLevel={selectedLocation.post.currentPostLevel}
          onLocationClick={handleLocationClick}
        />
      )}

      {/* Old Location Modal - Removed, using LocationModal component instead */}
    </Box>
  );
};

export default Posts; 